import BasicLink from "@/components/core/links/BasicLink";
import AlertPreferencesEmailModal from "@/components/core/modals/AlertPreferencesEmailModal";
import { ROUTES } from "@/routes/Routes";
import { MailRecipientDTO } from "@/state/alertsPreferences/dto/response/mail.recipient.dto";
import { PreferenceDTO } from "@/state/alertsPreferences/dto/response/preference.dto";
import { authenticationStore } from "@/state/auth/AuthStore";
import { AuthenticationContext } from "@/type/core/auth/auth.types";
import { AlertPreferenceDelayTypeEnum } from "@/utils/enums/alert.preference.delay.type.enum";
import { AlertPreferencesType } from "@/utils/enums/alert.preferences.type.enum";
import {
  ALERT_DAYS_ENUM,
  ALERT_DAYS_ENUM_SHORT,
  ALERT_LOW_CREDIT_ENUM,
} from "@utils/Constant";
import { Checkbox, Col, InputNumber, Row, Select } from "antd";
import { CheckboxChangeEvent } from "antd/lib/checkbox";
import { useUnit } from "effector-react";
import { FunctionComponent, RefObject, useState } from "react";
import { useTranslation } from "react-i18next";

const { Option } = Select;

interface MyPreferencesAlertProps {
  index: number;
  className?: string;
  recapPrefsRef: RefObject<HTMLDivElement>;
  preference: PreferenceDTO;
  onSaveAlertPreferences: (
    newPreference: PreferenceDTO,
    callback?: () => void,
  ) => void;
  alertFrontKey?: string;
  instantEmailPref?: boolean;
  delayType?: AlertPreferenceDelayTypeEnum;
  notificationClassicConfiguration?: boolean;
  noPeriodicitySelected: boolean;
  isMultipleThreshold?: boolean;
}

const MyPreferencesAlert: FunctionComponent<MyPreferencesAlertProps> = ({
  index,
  className = "",
  recapPrefsRef,
  preference,
  onSaveAlertPreferences,
  instantEmailPref,
  delayType,
  notificationClassicConfiguration,
  noPeriodicitySelected,
  isMultipleThreshold,
}) => {
  const { t } = useTranslation();

  const authStore = useUnit<AuthenticationContext | null>(authenticationStore);
  const userEmail = authStore?.user?.email;

  const [
    isAlertPreferencesEmailModalVisible,
    setIsAlertPreferencesEmailModalVisible,
  ] = useState<boolean>(false);
  const [localHomePage, setLocalHomePage] = useState<boolean | null>(
    preference.homePage,
  );
  const [localInstantEmail, setLocalInstantEmail] = useState<boolean | null>(
    preference.instantEmail,
  );
  const [localThreshold, setLocalThreshold] = useState<number | null>(
    preference.threshold,
  );
  const [localThresholds, setLocalThresholds] = useState<number[] | undefined>(
    preference.thresholds,
  );
  const [localDailyEmail, setLocalDailyEmail] = useState<boolean | null>(
    preference.dailyEmail,
  );

  const preferenceType = preference.type;
  const defaultMails: MailRecipientDTO[] = [
    { email: userEmail, recipientType: "TO" } as MailRecipientDTO,
  ];
  const emails: MailRecipientDTO[] =
    preference.email && preference.email.length > 0
      ? (preference.email ?? defaultMails)
      : defaultMails;

  const ENUM: number[] =
    delayType === AlertPreferenceDelayTypeEnum.AMOUNT
      ? ALERT_LOW_CREDIT_ENUM
      : isMultipleThreshold
        ? ALERT_DAYS_ENUM_SHORT
        : ALERT_DAYS_ENUM;
  const MAX_VALUE = 999;

  const handleClickScroll = () => {
    if (recapPrefsRef.current) {
      // Use the ref to scroll into view
      recapPrefsRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handleHomePageChange = (event: CheckboxChangeEvent) => {
    const homePage = event.target.checked;
    onSaveAlertPreferences({ ...preference, homePage }, () =>
      setLocalHomePage(homePage),
    );
  };

  const handleInstantEmailChange = (event: CheckboxChangeEvent) => {
    const instantEmail = event.target.checked;
    if (preference.email === null) {
      preference.email = defaultMails;
    }
    onSaveAlertPreferences(
      {
        ...preference,
        instantEmail,
      },
      () => setLocalInstantEmail(instantEmail),
    );
  };

  const handleThresholdChange = (threshold: number) => {
    onSaveAlertPreferences({ ...preference, threshold }, () =>
      setLocalThreshold(threshold),
    );
  };

  const handleThresholdsChange = (thresholds: number[]) => {
    if (isMultipleThreshold && thresholds.length === 0) {
      return;
    }
    onSaveAlertPreferences({ ...preference, thresholds }, () =>
      setLocalThresholds(thresholds),
    );
  };

  const handleDailyEmailChange = (event: CheckboxChangeEvent) => {
    const dailyEmail = event.target.checked;
    onSaveAlertPreferences({ ...preference, dailyEmail }, () =>
      setLocalDailyEmail(dailyEmail),
    );
  };

  const handleEmailsChange = (emails: MailRecipientDTO[]) => {
    onSaveAlertPreferences({ ...preference, email: emails });
  };

  const displayCorrectDelayType = (): string => {
    switch (delayType) {
      case AlertPreferenceDelayTypeEnum.AMOUNT:
        return t("account.myPreferences.delay.lowAccount");
      case AlertPreferenceDelayTypeEnum.DAYS_PASSED:
        return t("account.myPreferences.delay.plus");
      case AlertPreferenceDelayTypeEnum.DAYS_LEFT:
        return t("account.myPreferences.delay.minus");
      case AlertPreferenceDelayTypeEnum.DAYS_EXACTLY:
        return t("account.myPreferences.delay.exactly");
      case AlertPreferenceDelayTypeEnum.NOTHING:
      default:
        return "";
    }
  };

  const displaySingleOrMultipleSelect = () => {
    if (isMultipleThreshold) {
      return (
        <Select
          className={`w-100 mw-160 ${localThresholds?.length === 1 ? "only-one-value-left" : ""}`}
          mode={"multiple"}
          onChange={handleThresholdsChange}
          value={localThresholds}
          dropdownRender={() => (
            <>
              <Checkbox.Group
                value={localThresholds}
                onChange={(checkedValues) =>
                  handleThresholdsChange(checkedValues)
                }
                className="column-align-items-start"
              >
                {ENUM.map((option) => (
                  <Checkbox
                    key={`dropDownRender_Checkbox_${option}`}
                    value={option}
                    className="my-2"
                    disabled={
                      localThresholds?.length === 1 &&
                      localThresholds[0] === option
                    }
                  >
                    {option}
                  </Checkbox>
                ))}
              </Checkbox.Group>
            </>
          )}
        />
      );
    } else {
      return (
        <Select
          className="w-100"
          onChange={handleThresholdChange}
          value={localThreshold}
        >
          {ENUM.map((option) => (
            <Option key={option} value={option}>
              {option}
            </Option>
          ))}
        </Select>
      );
    }
  };

  return (
    <div
      className={`app-preferences-alert mb-32 ${className}`}
      data-test="section"
      data-alt={t(`account.myPreferences.type.${preferenceType}.label`)}
    >
      {index !== 0 && <hr className="pt-16 mx-16" />}
      <h2 className="text-black">
        {t(`account.myPreferences.type.${preferenceType}.label`)}
      </h2>
      <i>{t(`account.myPreferences.type.${preferenceType}.description`)}</i>
      <p className="mt-24 mb-8">{t("account.myPreferences.byType.channel")}</p>
      {notificationClassicConfiguration && (
        <Row gutter={16}>
          {localHomePage != null && (
            <Col
              span={24}
              xl={8}
              data-test={"input-label"}
              data-alt={t("account.myPreferences.channel.portal")}
              data-test-for={`${preferenceType}_displayInPortal`}
            >
              <Checkbox
                id={`${preferenceType}_displayInPortal`}
                checked={localHomePage}
                data-test={`${preferenceType}_displayInPortal`}
                onChange={handleHomePageChange}
              >
                {t("account.myPreferences.channel.portal")}
              </Checkbox>
            </Col>
          )}
          {localDailyEmail != null && (
            <Col
              span={24}
              xl={8}
              data-test={"input-label"}
              data-alt={t("account.myPreferences.channel.recap")}
              data-test-for={`${preferenceType}_includeInRecap`}
            >
              <Checkbox
                id={`${preferenceType}_includeInRecap`}
                data-test={`${preferenceType}_includeInRecap`}
                checked={localDailyEmail}
                onChange={handleDailyEmailChange}
              >
                {t("account.myPreferences.channel.recap")}
              </Checkbox>
              {noPeriodicitySelected && preference.dailyEmail && (
                <p
                  className="text-info font-small cursor-pointer"
                  onClick={() => {
                    handleClickScroll();
                  }}
                >
                  <u>
                    <i>
                      {t("account.myPreferences.channel.noPeriodicitySelected")}
                    </i>
                  </u>
                </p>
              )}
            </Col>
          )}
          {instantEmailPref && (
            <Col
              span={24}
              xl={8}
              data-test={"input-label"}
              data-alt={t("account.myPreferences.channel.instantMail")}
              data-test-for={`${preferenceType}_sendInstantMail`}
            >
              <Checkbox
                id={`${preferenceType}_sendInstantMail`}
                checked={localInstantEmail ?? false}
                data-test={`${preferenceType}_sendInstantMail`}
                onChange={handleInstantEmailChange}
              >
                {t("account.myPreferences.channel.instantMail")}
              </Checkbox>
              <ul className="mt-0">
                {emails?.map((e) => (
                  <li key={`${preferenceType}_${e.email}`}>
                    {`${t("account.myPreferences.recipientType." + e.recipientType)} : ${e.email}`}
                  </li>
                ))}
                <a onClick={() => setIsAlertPreferencesEmailModalVisible(true)}>
                  {t("account.myPreferences.channel.customizeAddress")}
                </a>
              </ul>
            </Col>
          )}
        </Row>
      )}
      {notificationClassicConfiguration &&
        delayType !== AlertPreferenceDelayTypeEnum.NOTHING && (
          <Row gutter={32} className="align-items-baseline mt-16">
            <Col span={24} sm={12} md={8} lg={6}>
              {displayCorrectDelayType()}
            </Col>
            <Col>
              {delayType === AlertPreferenceDelayTypeEnum.AMOUNT ? (
                <InputNumber
                  className="w-100"
                  max={MAX_VALUE}
                  maxLength={4}
                  value={localThreshold}
                  onChange={(value: number | null) =>
                    handleThresholdChange(value ?? 0)
                  }
                />
              ) : (
                displaySingleOrMultipleSelect()
              )}
            </Col>
            <Col>
              <label>
                {delayType === AlertPreferenceDelayTypeEnum.AMOUNT
                  ? t("account.myPreferences.delay.euros")
                  : t("account.myPreferences.delay.days")}
              </label>
            </Col>
          </Row>
        )}
      {!notificationClassicConfiguration &&
        preferenceType != AlertPreferencesType.DEGRADED_MODE && (
          <Row className="align-items-center custom-checkbox ml-4 mt-3">
            <Checkbox
              id={`${preferenceType}`}
              checked={preference.threshold === 1}
              onChange={() =>
                handleThresholdChange(preference.threshold === 1 ? 0 : 1)
              }
            >
              <label
                className="cursor-pointer mb-0 custom-control-label format text-wrap "
                htmlFor="customCheckbox"
              >
                <span>
                  {t(
                    `account.myPreferences.type.${preferenceType}.checkboxDescription`,
                  )}
                  <BasicLink
                    to={ROUTES.extranet.menu.offices.preferences.generate()}
                  >
                    {t(`account.myPreferences.type.${preferenceType}.link`)}
                  </BasicLink>
                  {t(
                    `account.myPreferences.type.${preferenceType}.checkboxDescription2`,
                  )}
                </span>
              </label>
            </Checkbox>
          </Row>
        )}
      {preferenceType === AlertPreferencesType.DEGRADED_MODE && (
        // Uniquement pour le mode dégradé (DEGRADED_MODE)
        <Col
          span={24}
          xl={8}
          data-test={"input-label"}
          data-alt={t("account.myPreferences.channel.instantMail")}
          data-test-for={`${preferenceType}_sendInstantMail`}
        >
          <Checkbox
            id={`${preferenceType}_sendInstantMail`}
            checked={localInstantEmail ?? false}
            data-test={`${preferenceType}_sendInstantMail`}
            onChange={handleInstantEmailChange}
          >
            {t("account.myPreferences.channel.instantMail")}
          </Checkbox>
          <ul className="mt-0">
            {emails?.map((e) => (
              <li key={`${preferenceType}_${e.email}`}>
                {`${t("account.myPreferences.recipientType." + e.recipientType)} : ${e.email}`}
              </li>
            ))}
            <a onClick={() => setIsAlertPreferencesEmailModalVisible(true)}>
              {t("account.myPreferences.channel.customizeAddress")}
            </a>
          </ul>
        </Col>
      )}
      {isAlertPreferencesEmailModalVisible && (
        <AlertPreferencesEmailModal
          mailRecipients={emails ?? []}
          visible={isAlertPreferencesEmailModalVisible}
          onCancel={() => setIsAlertPreferencesEmailModalVisible(false)}
          onSave={handleEmailsChange}
        />
      )}
    </div>
  );
};

export default MyPreferencesAlert;
